"use client";

import { Container, Title, Text, SimpleGrid, Card, Badge, Group, Stack, Button, Box } from "@mantine/core";
import { IconArrowRight } from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import { aestheticTypes, aestheticTypeIds } from "./aestheticData";
import { useTranslations } from "next-intl";

// Accent colors for visual variety
const accentColors: Record<string, string> = {
  "old-money": "indigo",
  "clean-girl": "teal",
  "dark-feminine": "grape",
  coquette: "pink",
  "quiet-luxury": "gray",
  "indie-sleaze": "red",
  "coastal-grandmother": "cyan",
  balletcore: "pink",
  "mob-wife": "yellow",
  cottagecore: "green",
  minimalist: "gray",
  streetwear: "orange",
};

export default function AestheticContent({ locale }: { locale: string }) {
  const t = useTranslations("aesthetic.page");
  return (
    <Container size="lg" py="xl">
      {/* Hero */}
      <Stack gap="md" ta="center" mb="xl">
        <Badge size="lg" variant="light" color="violet">
          {t("badge")}
        </Badge>
        <Title order={1} fz={{ base: 32, md: 42 }} fw={300}>
          {t("title")}
        </Title>
        <Text c="dimmed" maw={600} mx="auto">
          {t("subtitle")}
        </Text>
        <Button
          component={Link}
          href="/aesthetic-quiz"
          size="lg"
          color="violet"
          rightSection={<IconArrowRight size={18} />}
        >
          {t("takeQuiz")}
        </Button>
      </Stack>

      {/* Aesthetic Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
        {aestheticTypeIds.map((id) => {
          const aesthetic = aestheticTypes[id];
          const color = accentColors[id] || "violet";

          return (
            <Card
              key={id}
              component={Link}
              href={`/aesthetic/${id}`}
              withBorder
              padding="lg"
              radius="md"
              style={{ textDecoration: "none", transition: "transform 0.2s, box-shadow 0.2s" }}
              className="hover-card"
            >
              <Stack gap="sm">
                <Badge color={color} variant="light" size="sm" w="fit-content">
                  {aesthetic.name}
                </Badge>
                <Title order={3} fz="lg" fw={500}>
                  {aesthetic.name} {t("aestheticSuffix")}
                </Title>
                <Text fz="sm" c="dimmed" fs="italic" lineClamp={2}>
                  "{aesthetic.tagline}"
                </Text>
                <Group gap={4} wrap="wrap">
                  {aesthetic.colorPalette.slice(0, 4).map((color) => (
                    <Box
                      key={color.name}
                      w={20}
                      h={20}
                      style={{ backgroundColor: color.hex, borderRadius: 4, border: "1px solid #eee" }}
                    />
                  ))}
                </Group>
                <Text fz="xs" c="dimmed">
                  {aesthetic.celebrities.slice(0, 2).join(", ")}
                </Text>
              </Stack>
            </Card>
          );
        })}
      </SimpleGrid>

      {/* CTA */}
      <Box ta="center" mt="xl" pt="xl">
        <Text c="dimmed" mb="md">
          {t("notSure")}
        </Text>
        <Button
          component={Link}
          href="/aesthetic-quiz"
          size="lg"
          variant="light"
          color="violet"
          rightSection={<IconArrowRight size={18} />}
        >
          {t("takeFreeQuiz")}
        </Button>
      </Box>

      <style>{`
        .hover-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.1);
        }
      `}</style>
    </Container>
  );
}
